/**
 * @file VideoPlayerPage.js
 * @author Simon Tenedero, Jonas Matulis
 * @created 2024-XX-XX
 * @lastModified 2025-06-04
 * @description file containing the VideoPlayerPage component.
 */

import { useState, useEffect } from "react";
import { collection, query, onSnapshot, where } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { fetchChats } from "../utils/privateChatUtils";
import VideoPlayer from "../components/StreamPlayer/VideoPlayer";
import SwitchableChat from "../components/Chat/SwitchableChat";
import { useUser } from "../context/UserContext";
import { useFeatureFlags } from "../context/FeatureContext";
import { AVAILABLE_FEATURES } from "../utils/roleUtils";

/**
 * This component represents the livestream page, has two main components, the livestream itself, and the switchable chats
 *
 * @param {Object} props - The component properties
 * @param {string} props.videoUrl - The URL of the video to be played.
 * @param {Function} props.setVideoUrl - Function to set the video URL.
 * @param {Array} props.privateChats - Array of private chat objects.
 * @param {Function} props.setPrivateChats - Function to set the private chats.
 * @param {Object} props.selectedPrivateChat - The currently selected private chat.
 * @param {Function} props.setSelectedPrivateChat - Function to set the selected private chat
 * @param {Array} props.privateChatMembers - Map where key-val pairs are privateChatId and its members
 * @param {Function} props.setPrivateChatMembers - Function to set the private chat members
 * @param {Object} props.selectedPrivateChatMembers - The currently selected private chat members.
 * @param {Function} props.setSelectedPrivateChatMembers - Function to set the selected private chat members.
 * @param {Array} props.invitations - Array of invitation objects.
 * @param {Function} props.setInvitations - Function to set the invitations.
 * @param {Map} props.chatCacheRef - useRef variable to cache state of privateChats
 * @param {Object} props.selectedPrivateChatRef - useRef variable to cache state of current private chat, cannot use selectedPrivateChat directly because of closure (when listener if first established, it'll retain a stale copy of selectedPrivateChat)
 *
 * @returns {JSX.Element} The rendered VideoPlayerPage component
 */
const VideoPlayerPage = ({
  videoUrl,
  setVideoUrl,
  privateChats,
  setPrivateChats,
  selectedPrivateChat,
  setSelectedPrivateChat,
  privateChatMembers,
  setPrivateChatMembers,
  selectedPrivateChatMembers,
  setSelectedPrivateChatMembers,
  pendingPrivateChatMembers,
  setPendingPrivateChatMembers,
  selectedPrivateChatPendingMembers,
  setSelectedPrivateChatPendingMembers,
  invitations,
  setInvitations,
  chatCacheRef,
  selectedPrivateChatRef,
}) => {
  const [notification, setNotification] = useState("");
  const [chatOpen, setChatOpen] = useState(true); //tracking whether chat is collapsed or not
  const { user } = useUser();
  const [loadingChatSettings, setLoadingChatSettings] = useState(false); //loading state for chat settings
  const { loading, hasFeature } = useFeatureFlags();

  useEffect(() => {
    //must check we have the feature enabled, otherwise security rules will block access to reading privateChats collection
    if (!loading && hasFeature(AVAILABLE_FEATURES.PRIVATE_CHAT)) {
      console.log("hello trying to fetchChats");

      //NOW we need to create listener for private chats, fetch them from invitation list, where chatStatus is active.
      const unsubscribeChats = fetchChats(
        user,
        setPrivateChats,
        setSelectedPrivateChat,
        setPrivateChatMembers,
        setSelectedPrivateChatMembers,
        setPendingPrivateChatMembers,
        setSelectedPrivateChatPendingMembers,
        chatCacheRef,
        selectedPrivateChatRef,
        setLoadingChatSettings,
      );

      console.log("privateChats=", privateChats);

      //onSnapshot() sets a listener on a collection or document, will re-run code anytime data updates
      //this function constantly listens for any invites, and will update the invite list if needed
      const unsubscribeInvited = onSnapshot(
        query(
          collection(db, "users", user.uid, "invitations"),
          where("status", "==", "pending"),
          where("chatStatus", "==", "active"),
        ),
        (snapshot) => {
          const invites = snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }));

          console.log("invites", invites);
          setInvitations(invites);
        },
      );

      return () => {
        //cleanup function, runs whenever useEffect triggers, or whenever the component using the useEffect unmounts from DOM
        unsubscribeInvited();
        unsubscribeChats();
      };
    }
  }, [user]); //function called if user changes

  return (
    <div className="min-h-screen app-container flex flex-col">
      {/*only render notification div if notification exists*/}
      {notification && <div className="notification">{notification}</div>}
      {videoUrl ? (
        <div
          className={`h-full flex items-start flex-grow flex-wrap landscape:px-10 py-6 lg:py-10 ${chatOpen ? "justify-around" : "justify-center"}`}
        >
          <div
            className={`video-player-container ${chatOpen ? "" : "mx-auto"}`}
          >
            <VideoPlayer videoUrl={videoUrl} chatOpen={chatOpen} />
          </div>

          <div className="switchable-chat-container">
            <SwitchableChat
              videoUrl={videoUrl}
              setVideoUrl={setVideoUrl}
              privateChats={privateChats}
              selectedPrivateChat={selectedPrivateChat}
              setSelectedPrivateChat={setSelectedPrivateChat}
              privateChatMembers={privateChatMembers}
              selectedPrivateChatMembers={selectedPrivateChatMembers}
              setSelectedPrivateChatMembers={setSelectedPrivateChatMembers}
              pendingPrivateChatMembers={pendingPrivateChatMembers}
              setPendingPrivateChatMembers={setPendingPrivateChatMembers}
              selectedPrivateChatPendingMembers={
                selectedPrivateChatPendingMembers
              }
              setSelectedPrivateChatPendingMembers={
                setSelectedPrivateChatPendingMembers
              }
              invitations={invitations}
              chatOpen={chatOpen}
              setChatOpen={setChatOpen}
              loadingChatSettings={loadingChatSettings}
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-col justify-start min-h-screen xl:pt-20 desktop:pt-10 pl-10">
          <h1 className="my-auto text-5xl md:text-7xl">
            nothing to see here. <br /> try choosing a{" "}
            <span className="text-primary">livestream.</span>
          </h1>
        </div>
      )}
    </div>
  );
};

export default VideoPlayerPage;
